import { useEffect, useRef, useState, useCallback } from "react";
import Plotly from "plotly.js-dist-min";
import type { PlotData as CalcPlotData } from "@quanta/calcscript";
import type { Data, Layout, Config } from "plotly.js";

// Patch canvas getContext to add willReadFrequently for better performance
// This fixes: "Canvas2D: Multiple readback operations using getImageData are faster with willReadFrequently"
const originalGetContext = HTMLCanvasElement.prototype.getContext;
HTMLCanvasElement.prototype.getContext = function (
  this: HTMLCanvasElement,
  type: string,
  options?: CanvasRenderingContext2DSettings,
) {
  if (type === "2d") {
    return originalGetContext.call(this, type, {
      willReadFrequently: true,
      ...options,
    });
  }
  return originalGetContext.call(this, type, options);
} as typeof HTMLCanvasElement.prototype.getContext;

interface PlotViewerProps {
  data: CalcPlotData;
}

// ============================================================
// Theme definitions
// ============================================================
const THEMES: Record<
  string,
  {
    paper: string;
    plot: string;
    font: string;
    grid: string;
    zeroline: string;
    colorway: string[];
  }
> = {
  default: {
    paper: "rgba(30,30,30,0.8)",
    plot: "rgba(40,40,40,0.9)",
    font: "#e0e0e0",
    grid: "rgba(255,255,255,0.1)",
    zeroline: "rgba(255,255,255,0.2)",
    colorway: [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
    ],
  },
  dark: {
    paper: "#1a1a2e",
    plot: "#16213e",
    font: "#e0e0e0",
    grid: "rgba(255,255,255,0.08)",
    zeroline: "rgba(255,255,255,0.15)",
    colorway: [
      "#0f3460",
      "#e94560",
      "#533483",
      "#00b4d8",
      "#e77600",
      "#2ec4b6",
      "#ff6b6b",
      "#c77dff",
    ],
  },
  light: {
    paper: "#ffffff",
    plot: "#f8f9fa",
    font: "#333333",
    grid: "rgba(0,0,0,0.1)",
    zeroline: "rgba(0,0,0,0.2)",
    colorway: [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
    ],
  },
  seaborn: {
    paper: "#eaeaf2",
    plot: "#eaeaf2",
    font: "#333333",
    grid: "#ffffff",
    zeroline: "#cccccc",
    colorway: [
      "#4c72b0",
      "#dd8452",
      "#55a868",
      "#c44e52",
      "#8172b3",
      "#937860",
      "#da8bc3",
      "#8c8c8c",
    ],
  },
  ggplot: {
    paper: "#ffffff",
    plot: "#e5e5e5",
    font: "#333333",
    grid: "#ffffff",
    zeroline: "#cccccc",
    colorway: [
      "#e41a1c",
      "#377eb8",
      "#4daf4a",
      "#984ea3",
      "#ff7f00",
      "#a65628",
      "#f781bf",
      "#999999",
    ],
  },
  minimal: {
    paper: "rgba(30,30,30,0.8)",
    plot: "rgba(30,30,30,0.8)",
    font: "#cccccc",
    grid: "rgba(255,255,255,0.05)",
    zeroline: "rgba(255,255,255,0.1)",
    colorway: [
      "#4fc3f7",
      "#81c784",
      "#ffb74d",
      "#e57373",
      "#ba68c8",
      "#4db6ac",
      "#fff176",
      "#90a4ae",
    ],
  },
  presentation: {
    paper: "#1e1e1e",
    plot: "#2d2d2d",
    font: "#ffffff",
    grid: "rgba(255,255,255,0.15)",
    zeroline: "rgba(255,255,255,0.3)",
    colorway: [
      "#00bcd4",
      "#ff9800",
      "#4caf50",
      "#f44336",
      "#9c27b0",
      "#ffeb3b",
      "#2196f3",
      "#e91e63",
    ],
  },
  highcontrast: {
    paper: "#000000",
    plot: "#000000",
    font: "#ffffff",
    grid: "rgba(255,255,255,0.3)",
    zeroline: "rgba(255,255,255,0.5)",
    colorway: [
      "#00ff00",
      "#ff0000",
      "#ffff00",
      "#00ffff",
      "#ff00ff",
      "#ffffff",
      "#ff8800",
      "#88ff00",
    ],
  },
};

function getTheme(name?: string) {
  return THEMES[name || "default"] || THEMES.default;
}

// ============================================================
// Build Plotly traces from CalcPlotData
// ============================================================
function buildTraces(data: CalcPlotData): Data[] {
  if (data.plotType === "subplot" && data.subplots) {
    // Subplots are handled at the layout level, not here
    const traces: Data[] = [];
    data.subplots.forEach((sub, si) => {
      const subTraces = buildTraces(sub);
      subTraces.forEach((t) => {
        const trace = { ...t } as Record<string, unknown>;
        if (si > 0) {
          trace.xaxis = `x${si + 1}`;
          trace.yaxis = `y${si + 1}`;
        }
        traces.push(trace as Data);
      });
    });
    return traces;
  }

  return data.data.map((series, index) => {
    const baseName = series.label || `Series ${index + 1}`;

    if (data.plotType === "2d" || data.plotType === "scatter") {
      const defaultMode = data.plotType === "scatter" ? "markers" : "lines";
      const traceMode = (series.mode || defaultMode) as
        | "lines"
        | "markers"
        | "lines+markers";
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        type: "scatter" as const,
        mode: traceMode,
        ...(series.color && {
          line: { color: series.color },
          marker: { color: series.color },
        }),
        ...(series.fill && { fill: series.fill }),
        ...(series.fillcolor && { fillcolor: series.fillcolor }),
      };
    }

    if (data.plotType === "parametric") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        type: "scatter" as const,
        mode: "lines" as const,
        ...(series.color && { line: { color: series.color } }),
      };
    }

    if (data.plotType === "histogram") {
      return {
        name: baseName,
        x: series.values,
        type: "histogram" as const,
        nbinsx: series.bins,
      };
    }

    if (data.plotType === "bar") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        type: "bar" as const,
        ...(series.orientation && { orientation: series.orientation }),
      };
    }

    if (data.plotType === "stem") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        type: "bar" as const,
        width: 0.02,
        marker: { color: series.color || "#1f77b4" },
      };
    }

    if (data.plotType === "3d") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        z: series.z,
        type: "scatter3d" as const,
        mode: "lines" as const,
      };
    }

    if (data.plotType === "polar") {
      return {
        name: baseName,
        r: series.r,
        theta: series.theta,
        type: "scatterpolar" as const,
        mode: "lines" as const,
      };
    }

    if (data.plotType === "surface") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        z: series.z,
        type: "surface" as const,
      };
    }

    if (data.plotType === "mesh") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        z: series.z,
        type: "surface" as const,
        hidesurface: true,
        contours: {
          x: { show: true },
          y: { show: true },
          z: { show: true },
        },
      };
    }

    if (data.plotType === "contour") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        z: series.z,
        type: "contour" as const,
      };
    }

    if (data.plotType === "heatmap") {
      return {
        name: baseName,
        z: series.z,
        type: "heatmap" as const,
        colorscale: "Greys",
        showscale: false,
      };
    }

    // Vector field (quiver) rendered as arrow line segments
    if (data.plotType === "vectorfield") {
      const x = series.x as number[];
      const y = series.y as number[];
      const u = (series.u || []) as number[];
      const v = (series.v || []) as number[];

      // Compute grid spacing and normalize arrows
      const maxMag = Math.max(
        ...u.map((ui, i) => Math.sqrt(ui * ui + (v[i] || 0) * (v[i] || 0))),
        1e-10,
      );
      const xSpan = Math.max(...x) - Math.min(...x) || 1;
      const ySpan = Math.max(...y) - Math.min(...y) || 1;
      const gridSpacing = Math.max(xSpan, ySpan) / Math.sqrt(x.length);
      const scale = 0.8 * gridSpacing;

      // Build arrow lines: base → tip + arrowhead barbs
      const arrowX: (number | null)[] = [];
      const arrowY: (number | null)[] = [];
      for (let i = 0; i < x.length; i++) {
        const mag = Math.sqrt(u[i] * u[i] + (v[i] || 0) * (v[i] || 0));
        if (mag < 1e-10) continue;
        const nx = (u[i] / maxMag) * scale;
        const ny = ((v[i] || 0) / maxMag) * scale;
        const tipX = x[i] + nx;
        const tipY = y[i] + ny;
        // Shaft
        arrowX.push(x[i], tipX, null);
        arrowY.push(y[i], tipY, null);
        // Arrowhead barbs (two short lines from tip)
        const ax1 = -nx * 0.25 + ny * 0.2;
        const ay1 = -ny * 0.25 - nx * 0.2;
        const ax2 = -nx * 0.25 - ny * 0.2;
        const ay2 = -ny * 0.25 + nx * 0.2;
        arrowX.push(tipX + ax1, tipX, tipX + ax2, null);
        arrowY.push(tipY + ay1, tipY, tipY + ay2, null);
      }

      return {
        name: baseName,
        x: arrowX,
        y: arrowY,
        type: "scatter" as const,
        mode: "lines" as const,
        line: { color: series.color || "#4fc3f7", width: 1.5 },
        hoverinfo: "skip",
      };
    }

    // Streamline plot
    if (data.plotType === "streamline") {
      return {
        name: baseName,
        x: series.x,
        y: series.y,
        type: "scatter" as const,
        mode: "lines" as const,
        line: { color: series.color || "#81c784", width: 1 },
        hoverinfo: "skip",
        showlegend: false,
      };
    }

    // Box plot
    if (data.plotType === "box") {
      return {
        name: baseName,
        y: series.y,
        type: "box" as const,
        boxpoints: series.boxpoints || "outliers",
        jitter: series.jitter ?? 0.3,
        pointpos: series.pointpos ?? -1.5,
        marker: { color: series.color || undefined },
      };
    }

    // Violin plot
    if (data.plotType === "violin") {
      return {
        name: baseName,
        y: series.y,
        type: "violin" as const,
        box: { visible: true },
        meanline: series.meanline || { visible: true },
        scalemode: series.scalemode || "count",
        side: series.side || undefined,
        marker: { color: series.color || undefined },
      };
    }

    // Default fallback
    return {
      name: baseName,
      x: series.x,
      y: series.y,
      type: "scatter" as const,
      mode: "lines" as const,
    };
  }) as Data[];
}

// ============================================================
// Build layout from CalcPlotData
// ============================================================
function buildLayout(data: CalcPlotData): Partial<Layout> {
  const theme = getTheme(data.theme);

  const layout: Partial<Layout> & Record<string, unknown> = {
    title: data.title ? { text: data.title } : undefined,
    showlegend: data.data.some((s) => s.label),
    margin: { l: 50, r: 30, t: 40, b: 40 },
    paper_bgcolor: theme.paper,
    plot_bgcolor: theme.plot,
    font: { color: theme.font },
    colorway: theme.colorway,
    autosize: true,
  };

  // Standard 2D axes
  if (data.plotType !== "polar") {
    layout.xaxis = {
      title: data.xlabel ? { text: data.xlabel } : undefined,
      range:
        data.axisLimits?.xmin != null && data.axisLimits?.xmax != null
          ? [data.axisLimits.xmin, data.axisLimits.xmax]
          : undefined,
      gridcolor: theme.grid,
      zerolinecolor: theme.zeroline,
    };
    layout.yaxis = {
      title: data.ylabel ? { text: data.ylabel } : undefined,
      range:
        data.axisLimits?.ymin != null && data.axisLimits?.ymax != null
          ? [data.axisLimits.ymin, data.axisLimits.ymax]
          : undefined,
      gridcolor: theme.grid,
      zerolinecolor: theme.zeroline,
    };
  }

  // Parametric: equal aspect ratio
  if (data.plotType === "parametric") {
    (layout as Record<string, unknown>).yaxis = {
      ...((layout as Record<string, unknown>).yaxis as object),
      scaleanchor: "x",
      scaleratio: 1,
    };
  }

  // Vector field / streamline: constrained axes with explicit range from data
  if (data.plotType === "vectorfield" || data.plotType === "streamline") {
    // Compute data extent from all series
    let allX: number[] = [];
    let allY: number[] = [];
    for (const s of data.data) {
      if (s.x) {
        const flat = (
          Array.isArray(s.x[0]) ? (s.x as number[][]).flat() : (s.x as number[])
        ).filter((v): v is number => v !== null);
        allX = allX.concat(flat);
      }
      if (s.y) {
        const flat = (
          Array.isArray(s.y[0]) ? (s.y as number[][]).flat() : (s.y as number[])
        ).filter((v): v is number => v !== null);
        allY = allY.concat(flat);
      }
    }
    if (allX.length > 0 && allY.length > 0) {
      const xMin = Math.min(...allX);
      const xMax = Math.max(...allX);
      const yMin = Math.min(...allY);
      const yMax = Math.max(...allY);
      const pad = Math.max(xMax - xMin, yMax - yMin) * 0.1 || 0.5;
      (layout as Record<string, unknown>).xaxis = {
        ...((layout as Record<string, unknown>).xaxis as object),
        range: [xMin - pad, xMax + pad],
        constrain: "domain",
      };
      (layout as Record<string, unknown>).yaxis = {
        ...((layout as Record<string, unknown>).yaxis as object),
        range: [yMin - pad, yMax + pad],
        scaleanchor: "x",
        scaleratio: 1,
        constrain: "domain",
      };
    }
  }

  // Annotations
  if (data.annotations && data.annotations.length > 0) {
    layout.annotations = data.annotations.map((a) => ({
      text: a.text,
      x: a.x,
      y: a.y,
      showarrow: a.showarrow !== false,
      arrowhead: a.arrowhead ?? 2,
      ax: a.ax ?? 0,
      ay: a.ay ?? -40,
      font: (a.font as Partial<Layout["font"]>) || {
        color: theme.font,
        size: 12,
      },
    }));
  }

  // Shapes
  if (data.shapes && data.shapes.length > 0) {
    layout.shapes = data.shapes.map((s) => ({
      type: s.type as "line" | "rect" | "circle",
      x0: s.x0,
      y0: s.y0,
      x1: s.x1,
      y1: s.y1,
      xref: (s.xref || "x") as "x",
      yref: (s.yref || "y") as "y",
      line: (s.line as { color?: string; width?: number }) || {
        color: "rgba(255,255,255,0.5)",
        width: 2,
      },
      fillcolor: s.fillcolor || "rgba(255,255,255,0.1)",
      opacity: s.opacity ?? 1,
    }));
  }

  // Subplot grid layout
  if (data.plotType === "subplot" && data.subplots && data.rows && data.cols) {
    const rows = data.rows;
    const cols = data.cols;
    layout.grid = { rows, columns: cols, pattern: "independent" as const };

    data.subplots.forEach((sub, si) => {
      if (si === 0) return; // First subplot uses default xaxis/yaxis
      const axisKey = si + 1;
      (layout as Record<string, unknown>)[`xaxis${axisKey}`] = {
        gridcolor: theme.grid,
        zerolinecolor: theme.zeroline,
        title: sub.xlabel ? { text: sub.xlabel } : undefined,
      };
      (layout as Record<string, unknown>)[`yaxis${axisKey}`] = {
        gridcolor: theme.grid,
        zerolinecolor: theme.zeroline,
        title: sub.ylabel ? { text: sub.ylabel } : undefined,
      };
    });
  }

  // Sliders (interactive_plot with pre-computed combinatorial frames)
  if (data.sliders && data.sliders.length > 0 && !data.animationOptions) {
    layout.sliders = data.sliders.map((slider, si) => ({
      active: Math.round((slider.value - slider.min) / slider.step),
      currentvalue: {
        prefix: `${slider.label || slider.param}: `,
        visible: true,
        xanchor: "center",
      },
      pad: { t: 50 + si * 40 },
      steps: Array.from(
        { length: Math.round((slider.max - slider.min) / slider.step) + 1 },
        (_, i) => {
          const val = slider.min + i * slider.step;
          return {
            label: val.toFixed(2),
            method: "skip" as const,
            args: [],
            value: String(val),
          };
        },
      ),
      x: 0.1,
      len: 0.8,
      xanchor: "left" as const,
      y: -0.1 - si * 0.15,
      yanchor: "top" as const,
    }));
    // Extra bottom margin for sliders
    layout.margin = { l: 50, r: 30, t: 40, b: 40 + data.sliders.length * 50 };
  }

  // Animation controls
  if (data.frames && data.frames.length > 0) {
    layout.updatemenus = [
      {
        type: "buttons" as const,
        showactive: false,
        x: 0.05,
        y: 1.15,
        xanchor: "left" as const,
        yanchor: "top" as const,
        buttons: [
          {
            label: "▶ Play",
            method: "animate" as const,
            args: [
              null,
              {
                frame: {
                  duration: data.animationOptions?.frameDuration ?? 50,
                  redraw: true,
                },
                transition: {
                  duration: data.animationOptions?.transitionDuration ?? 0,
                },
                fromcurrent: true,
                mode: "immediate" as const,
              },
            ],
          },
          {
            label: "⏸ Pause",
            method: "animate" as const,
            args: [
              [null],
              {
                frame: { duration: 0, redraw: false },
                mode: "immediate" as const,
                transition: { duration: 0 },
              },
            ],
          },
        ],
      },
    ];

    // Frame scrubber slider
    layout.sliders = [
      {
        active: 0,
        currentvalue: { prefix: "Frame: ", visible: true, xanchor: "center" },
        pad: { t: 50 },
        steps: data.frames.map((frame, i) => ({
          label: String(i),
          method: "animate" as const,
          args: [
            [frame.name],
            {
              mode: "immediate" as const,
              frame: { duration: 0, redraw: true },
              transition: { duration: 0 },
            },
          ],
        })),
        x: 0.1,
        len: 0.8,
        xanchor: "left" as const,
        y: -0.05,
        yanchor: "top" as const,
      },
    ];
    layout.margin = { l: 50, r: 30, t: 60, b: 80 };
  }

  return layout as Partial<Layout>;
}

export function PlotViewer({ data }: PlotViewerProps) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (!plotRef.current || !data.exportFormat) return;
    setExporting(true);
    try {
      const fmt = data.exportFormat.toLowerCase();
      const opts = {
        format: (fmt === "pdf" ? "svg" : fmt) as
          | "png"
          | "svg"
          | "jpeg"
          | "webp",
        width: data.exportWidth || 800,
        height: data.exportHeight || 600,
      };
      const url = await Plotly.toImage(plotRef.current, opts);

      const link = document.createElement("a");
      link.download = `plot.${fmt}`;
      link.href = url;
      link.click();
    } finally {
      setExporting(false);
    }
  }, [data]);

  useEffect(() => {
    if (!plotRef.current || !data.data) return;

    const plotlyData = buildTraces(data);
    const layout = buildLayout(data);

    const config: Partial<Config> = {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ["lasso2d", "select2d"],
    };

    // Build frames for animation
    const frames = data.frames?.map((f) => ({
      name: f.name,
      data: f.data.map((s) => ({
        x: s.x,
        y: s.y,
      })),
    }));

    if (frames && frames.length > 0) {
      Plotly.newPlot(plotRef.current, plotlyData, layout, config).then(() => {
        if (plotRef.current) {
          Plotly.addFrames(plotRef.current, frames as Plotly.Frame[]);

          // For interactive sliders: listen for slider changes and animate to
          // the combinatorial frame matching all current slider positions
          if (
            data.sliders &&
            data.sliders.length > 0 &&
            !data.animationOptions
          ) {
            const el = plotRef.current as HTMLDivElement & {
              _sliderIndices?: number[];
            };
            // Track current slider indices
            el._sliderIndices = data.sliders.map((s) =>
              Math.round((s.value - s.min) / s.step),
            );

            el.addEventListener("plotly_sliderchange", ((evt: CustomEvent) => {
              const sliderIndex = evt.detail?.slider?._index ?? 0;
              const stepIndex = evt.detail?.step?._index ?? 0;
              if (!el._sliderIndices) return;
              el._sliderIndices[sliderIndex] = stepIndex;
              const frameName = `frame_${el._sliderIndices.join("_")}`;
              Plotly.animate(el, [frameName], {
                mode: "immediate",
                frame: { duration: 0, redraw: true },
                transition: { duration: 50 },
              });
            }) as EventListener);
          }
        }
      });
    } else {
      Plotly.newPlot(plotRef.current, plotlyData, layout, config);
    }

    return () => {
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [data]);

  // Auto-export if exportFormat is set
  useEffect(() => {
    if (data.exportFormat && plotRef.current) {
      const timer = setTimeout(handleExport, 500);
      return () => clearTimeout(timer);
    }
  }, [data.exportFormat, handleExport]);

  const height =
    data.plotType === "subplot"
      ? `${Math.max(300, (data.rows || 1) * 250)}px`
      : "300px";

  return (
    <div className="plot-viewer" style={{ position: "relative" }}>
      <div
        ref={plotRef}
        style={{ width: "100%", height, minHeight: "300px" }}
      />
      {data.exportFormat && (
        <button
          className="plot-export-btn"
          onClick={handleExport}
          disabled={exporting}
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            padding: "4px 8px",
            fontSize: "11px",
            background: "rgba(60,60,60,0.9)",
            color: "#e0e0e0",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          {exporting
            ? "Exporting..."
            : `Export ${data.exportFormat.toUpperCase()}`}
        </button>
      )}
    </div>
  );
}
